import React, { createContext, useState, useContext, useEffect } from 'react';
import authService from '../services/authService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Carica l'utente al mount se esiste un token
  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = authService.getToken();

        if (token) {
          // Try to get from storage first for immediate render
          const storedUser = authService.getUser();
          if (storedUser && storedUser.id) {
            setUser(storedUser);
          }

          // Then fetch fresh profile
          const userProfile = await authService.getProfile();
          setUser(userProfile);
          // console.log('User profile loaded:', userProfile);
        }
      } catch (error) {
        console.error('Errore inizializzazione auth:', error);
        // If profile fetch fails (e.g. 401), maybe clear token? 
        // For now, let interceptors handle 401 logout
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  /**
   * Registrazione
   */
  const register = async (name, email, password, passwordConfirmation, inviteToken = null) => {
    try {
      const enrolledCourse = await authService.register(name, email, password, passwordConfirmation, inviteToken);

      // Aggiorna lo stato utente dal localStorage (authService ha già salvato token+user)
      const storedUser = authService.getUser();
      if (storedUser) {
        setUser(storedUser);
      }

      // Restituisce il corso in cui lo studente è stato iscritto (se presente)
      return enrolledCourse;
    } catch (error) {
      throw error;
    }
  };

  /**
   * Login
   */
  const login = async (email, password, inviteToken = null) => {
    try {
      const data = await authService.login(email, password, inviteToken);
      // Supporto per risposta wrappata
      const userData = data.data || data.user || (data.token ? data : null);

      if (userData) {
        setUser(userData);
      }

      return data;
    } catch (error) {
      throw error;
    }
  };

  /**
   * Logout
   */
  const logout = async () => {
    try {
      await authService.logout();
      setUser(null);
    } catch (error) {
      console.error('Errore logout:', error);
      // Anche in caso di errore, rimuovi l'utente
      setUser(null);
    }
  };

  /**
   * Verifica se l'utente è autenticato
   */
  const isAuthenticated = () => {
    return authService.isAuthenticated();
  };

  const value = {
    user,
    loading,
    register,
    login,
    logout,
    isAuthenticated,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook personalizzato per usare il context di autenticazione
 */
export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth deve essere usato all\'interno di AuthProvider');
  }

  return context;
};

export default AuthContext;
